import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, Clock, Palmtree, Save, CalendarX2, Users, X, CalendarRange, Coffee, Plus } from "lucide-react";
import { useAuth, API } from "../context/AuthContext";

const parseHHMM = (v) => {
  const [h, m] = String(v || "0:0").split(":");
  return parseInt(h, 10) * 60 + parseInt(m, 10);
};
const fmt = (mins) => `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;

const buildSlots = (start, end, interval, breaks) => {
  const s = parseHHMM(start);
  const e = parseHHMM(end);
  const out = [];
  if (isNaN(s) || isNaN(e) || !interval) return out;
  const br = (breaks || [])
    .filter((b) => b && b.start && b.end)
    .map((b) => [parseHHMM(b.start), parseHHMM(b.end)]);
  let t = s;
  while (t + interval <= e) {
    const overlaps = br.some(([bs, be]) => t < be && t + interval > bs);
    if (!overlaps) out.push(`${fmt(t)}\u2013${fmt(t + interval)}`);
    t += interval;
  }
  return out;
};

export const SchedulingSettings = ({ settings, onSaved, onAuthError }) => {
  const { authHeader } = useAuth();
  const weekdayNames = settings?.weekday_names || ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"];
  const intervalOptions = settings?.interval_options || [15, 30, 45, 60];
  const durationOptions = settings?.holiday_duration_options || [1, 3, 5, 7];

  const [weekdays, setWeekdays] = useState(settings?.pickup_weekdays || [4, 5, 6]);
  const [daySchedules, setDaySchedules] = useState(settings?.day_schedules || {});
  const [holidayStart, setHolidayStart] = useState(settings?.holiday_start || "");
  const [holidayDays, setHolidayDays] = useState(settings?.holiday_days || 0);
  const [blockedDates, setBlockedDates] = useState(settings?.blocked_dates || []);
  const [newBlocked, setNewBlocked] = useState("");
  const [slotCapacity, setSlotCapacity] = useState(settings?.slot_capacity ?? 0);
  const [dailyLimit, setDailyLimit] = useState(settings?.daily_order_limit ?? 0);
  const [bookingWeeks, setBookingWeeks] = useState(settings?.booking_weeks || 4);
  const [busy, setBusy] = useState(false);
  const [fetchedAt, setFetchedAt] = useState(null);

  const maxWeeks = settings?.max_booking_weeks || 12;

  useEffect(() => {
    if (!settings) return;
    setWeekdays(settings.pickup_weekdays || [4, 5, 6]);
    setDaySchedules(settings.day_schedules || {});
    setHolidayStart(settings.holiday_start || "");
    setHolidayDays(settings.holiday_days || 0);
    setBlockedDates(settings.blocked_dates || []);
    setSlotCapacity(settings.slot_capacity ?? 0);
    setDailyLimit(settings.daily_order_limit ?? 0);
    setBookingWeeks(settings.booking_weeks || 4);
    setFetchedAt(new Date());
  }, [settings]);

  const dayCfg = (wd) => {
    const c = daySchedules[String(wd)] || { slot_start: "14:00", slot_end: "19:00", interval: 60 };
    return { breaks: [], ...c };
  };
  const setDayField = (wd, field, value) =>
    setDaySchedules((prev) => ({
      ...prev,
      [String(wd)]: { ...dayCfg(wd), [field]: field === "interval" ? Number(value) : value },
    }));

  const addBreak = (wd) =>
    setDaySchedules((prev) => {
      const c = { breaks: [], ...(prev[String(wd)] || { slot_start: "14:00", slot_end: "19:00", interval: 60 }) };
      return { ...prev, [String(wd)]: { ...c, breaks: [...c.breaks, { start: "16:00", end: "17:00" }] } };
    });
  const setBreakField = (wd, idx, field, value) =>
    setDaySchedules((prev) => {
      const c = { breaks: [], ...(prev[String(wd)] || { slot_start: "14:00", slot_end: "19:00", interval: 60 }) };
      const breaks = c.breaks.map((b, i) => (i === idx ? { ...b, [field]: value } : b));
      return { ...prev, [String(wd)]: { ...c, breaks } };
    });
  const removeBreak = (wd, idx) =>
    setDaySchedules((prev) => {
      const c = { breaks: [], ...(prev[String(wd)] || { slot_start: "14:00", slot_end: "19:00", interval: 60 }) };
      return { ...prev, [String(wd)]: { ...c, breaks: c.breaks.filter((_, i) => i !== idx) } };
    });

  const toggleDay = (i) => {
    setWeekdays((prev) => (prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i].sort((a, b) => a - b)));
    setDaySchedules((prev) => (prev[String(i)] ? prev : { ...prev, [String(i)]: { slot_start: "14:00", slot_end: "19:00", interval: 60, breaks: [] } }));
  };

  const holidayEnd = useMemo(() => {
    if (!holidayStart || !holidayDays) return null;
    try {
      const d = new Date(holidayStart + "T00:00:00");
      d.setDate(d.getDate() + holidayDays - 1);
      return d;
    } catch (e) {
      return null;
    }
  }, [holidayStart, holidayDays]);

  const holidayResume = useMemo(() => {
    if (!holidayEnd) return null;
    const r = new Date(holidayEnd);
    r.setDate(r.getDate() + 1);
    return r;
  }, [holidayEnd]);

  const fmtDate = (d) => (d ? d.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" }) : "");

  const addBlocked = () => {
    if (!newBlocked) return;
    if (!blockedDates.includes(newBlocked)) {
      setBlockedDates((prev) => [...prev, newBlocked].sort());
    }
    setNewBlocked("");
  };
  const removeBlocked = (d) => setBlockedDates((prev) => prev.filter((x) => x !== d));

  const save = async () => {
    if (weekdays.length === 0) { toast.error("Kies minimaal één ophaaldag."); return; }
    for (const wd of weekdays) {
      const c = dayCfg(wd);
      if (parseHHMM(c.slot_start) >= parseHHMM(c.slot_end)) {
        toast.error(`${weekdayNames[wd]}: eindtijd moet na begintijd liggen.`); return;
      }
      for (const b of (c.breaks || [])) {
        if (!b.start || !b.end || parseHHMM(b.start) >= parseHHMM(b.end)) {
          toast.error(`${weekdayNames[wd]}: pauze eindtijd moet na begintijd liggen.`); return;
        }
      }
      if (buildSlots(c.slot_start, c.slot_end, c.interval, c.breaks).length === 0) {
        toast.error(`${weekdayNames[wd]}: na de pauze(s) blijven er geen tijdsloten over.`); return;
      }
    }
    if (holidayDays > 0 && !holidayStart) { toast.error("Kies een startdatum voor de vakantiemodus."); return; }
    const ds = {};
    weekdays.forEach((wd) => { ds[String(wd)] = dayCfg(wd); });
    setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/admin/settings`, {
        pickup_weekdays: weekdays,
        day_schedules: ds,
        holiday_days: Number(holidayDays),
        holiday_start: holidayDays > 0 ? holidayStart : "",
        blocked_dates: blockedDates,
        slot_capacity: Math.max(0, Number(slotCapacity) || 0),
        daily_order_limit: Math.max(0, Number(dailyLimit) || 0),
        booking_weeks: Math.max(1, Math.min(maxWeeks, Number(bookingWeeks) || 4)),
      }, { headers: authHeader() });
      onSaved?.(data);
      toast.success("Planning & beschikbaarheid opgeslagen.");
    } catch (e) {
      if (e?.response?.status === 401) onAuthError?.();
      else toast.error(e?.response?.data?.detail || "Opslaan mislukt.");
    } finally {
      setBusy(false);
    }
  };

  const chip = (active) =>
    `px-4 py-2 font-[700] text-sm border-2 transition-colors ${
      active ? "bg-[#1B6B2E] text-white border-[#1B6B2E]" : "bg-white text-[#0D3B4C] border-[#1B6B2E] hover:bg-[#E8F5E9]"
    }`;
  const inputCls = "border-2 border-[#0D3B4C] px-3 py-2 font-[700] text-[#0D3B4C] focus:border-[#B71C1C] focus:outline-none";

  return (
    <div className="space-y-6" data-testid="scheduling-settings">
      {/* Pickup days */}
      <div className="bg-[#FEF9E7] p-5">
        <div className="flex items-center gap-2 mb-1">
          <CalendarDays size={20} className="text-[#1B6B2E]" />
          <h2 className="font-[700] text-[#0D3B4C]">Ophaaldagen</h2>
        </div>
        <p className="text-sm text-[#0D3B4C] mb-4">Vink de dagen aan waarop klanten kunnen afhalen. Alleen deze dagen worden op de bestelpagina getoond.</p>
        <div className="flex flex-wrap gap-2">
          {weekdayNames.map((name, i) => (
            <button
              key={i}
              type="button"
              onClick={() => toggleDay(i)}
              data-testid={`pickup-day-checkbox-${i}`}
              aria-pressed={weekdays.includes(i)}
              className={chip(weekdays.includes(i))}
            >
              {name}
            </button>
          ))}
        </div>
      </div>

      {/* Time slots per day */}
      <div className="bg-[#FEF9E7] p-5">
        <div className="flex items-center gap-2 mb-1">
          <Clock size={20} className="text-[#1B6B2E]" />
          <h2 className="font-[700] text-[#0D3B4C]">Ophaal-tijdsloten per dag</h2>
        </div>
        <p className="text-sm text-[#0D3B4C] mb-4">Stel voor elke verkoopdag een eigen begintijd, eindtijd en interval in. De tijdsloten worden per dag automatisch opgebouwd.</p>
        {weekdays.length === 0 ? (
          <p className="text-sm font-[600] text-[#B71C1C]">Kies eerst een ophaaldag hierboven.</p>
        ) : (
          <div className="space-y-3" data-testid="day-schedules">
            {[...weekdays].sort((a, b) => a - b).map((wd) => {
              const c = dayCfg(wd);
              const preview = buildSlots(c.slot_start, c.slot_end, c.interval, c.breaks);
              return (
                <div key={wd} className="bg-white border-2 border-[#E8F5E9] p-4" data-testid={`day-schedule-${wd}`}>
                  <div className="flex flex-wrap items-end gap-4">
                    <div className="w-24">
                      <span className="font-[700] text-[#0D3B4C]">{weekdayNames[wd]}</span>
                    </div>
                    <div>
                      <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Begintijd</label>
                      <input type="time" value={c.slot_start} onChange={(e) => setDayField(wd, "slot_start", e.target.value)} data-testid={`slot-start-input-${wd}`} className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Eindtijd</label>
                      <input type="time" value={c.slot_end} onChange={(e) => setDayField(wd, "slot_end", e.target.value)} data-testid={`slot-end-input-${wd}`} className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Interval</label>
                      <select value={c.interval} onChange={(e) => setDayField(wd, "interval", e.target.value)} data-testid={`pickup-interval-select-${wd}`} className={inputCls}>
                        {intervalOptions.map((o) => (
                          <option key={o} value={o}>{o} minuten</option>
                        ))}
                      </select>
                    </div>
                    <div className="text-sm font-[600] text-[#1B6B2E] pb-2" data-testid={`day-slot-count-${wd}`}>
                      {preview.length} {preview.length === 1 ? "slot" : "sloten"}
                    </div>
                  </div>

                  {/* Breaks / pauzes */}
                  <div className="mt-3 border-t-2 border-[#E8F5E9] pt-3" data-testid={`day-breaks-${wd}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Coffee size={16} className="text-[#B71C1C]" />
                        <span className="text-xs font-[700] text-[#0D3B4C] uppercase tracking-wide">Pauzes (worden uit de tijdsloten gehaald)</span>
                      </div>
                      <button type="button" onClick={() => addBreak(wd)} data-testid={`add-break-${wd}`}
                        className="inline-flex items-center gap-1 bg-[#B71C1C] text-white text-xs font-[700] px-3 py-1.5 hover:bg-[#8E1515] transition-colors">
                        <Plus size={14} /> Pauze
                      </button>
                    </div>
                    {(c.breaks || []).length === 0 ? (
                      <p className="text-xs font-[600] text-[#0D3B4C]/70">Geen pauzes ingesteld.</p>
                    ) : (
                      <div className="space-y-2">
                        {(c.breaks || []).map((b, idx) => (
                          <div key={idx} className="flex flex-wrap items-end gap-3" data-testid={`break-row-${wd}-${idx}`}>
                            <div>
                              <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Van</label>
                              <input type="time" value={b.start} onChange={(e) => setBreakField(wd, idx, "start", e.target.value)} data-testid={`break-start-${wd}-${idx}`} className={inputCls} />
                            </div>
                            <div>
                              <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Tot</label>
                              <input type="time" value={b.end} onChange={(e) => setBreakField(wd, idx, "end", e.target.value)} data-testid={`break-end-${wd}-${idx}`} className={inputCls} />
                            </div>
                            <button type="button" onClick={() => removeBreak(wd, idx)} aria-label="Pauze verwijderen" data-testid={`break-remove-${wd}-${idx}`}
                              className="mb-1 inline-flex items-center gap-1 border-2 border-[#B71C1C] text-[#B71C1C] text-xs font-[700] px-3 py-2 hover:bg-[#B71C1C] hover:text-white transition-colors">
                              <X size={14} /> Verwijder
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2 mt-3">
                    {preview.length === 0 ? (
                      <span className="text-sm font-[600] text-[#B71C1C]">Geen geldige tijdsloten.</span>
                    ) : (
                      preview.map((s) => (
                        <span key={s} className="bg-[#FEF9E7] border-2 border-[#E8F5E9] px-2.5 py-1 text-xs font-[700] text-[#0D3B4C]">{s}</span>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Holiday mode */}
      <div className="bg-[#FEF9E7] p-5">
        <div className="flex items-center gap-2 mb-1">
          <Palmtree size={20} className="text-[#B71C1C]" />
          <h2 className="font-[700] text-[#0D3B4C]">Vakantiemodus</h2>
        </div>
        <p className="text-sm text-[#0D3B4C] mb-4">Blokkeer een aaneengesloten periode. Die datums verdwijnen automatisch van de bestelpagina en klanten zien een vriendelijke melding.</p>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Startdatum</label>
            <input type="date" value={holidayStart} onChange={(e) => setHolidayStart(e.target.value)} data-testid="holiday-start-input" className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Duur</label>
            <select value={holidayDays} onChange={(e) => setHolidayDays(Number(e.target.value))} data-testid="holiday-duration-select" className={inputCls}>
              <option value={0}>Uit</option>
              {durationOptions.map((o) => (
                <option key={o} value={o}>{o} {o === 1 ? "dag" : "dagen"}</option>
              ))}
            </select>
          </div>
        </div>
        {holidayDays > 0 && holidayEnd && (
          <div className="mt-4 bg-white border-2 border-[#B71C1C] p-3 text-sm text-[#0D3B4C]" data-testid="holiday-preview">
            Geblokkeerd van <b>{fmtDate(new Date(holidayStart + "T00:00:00"))}</b> t/m <b>{fmtDate(holidayEnd)}</b>.
            Klanten kunnen weer bestellen vanaf <b>{fmtDate(holidayResume)}</b>.
          </div>
        )}
      </div>

      {/* Booking window (weeks ahead) */}
      <div className="bg-[#FEF9E7] p-5">
        <div className="flex items-center gap-2 mb-1">
          <CalendarRange size={20} className="text-[#1B6B2E]" />
          <h2 className="font-[700] text-[#0D3B4C]">Vooruit bestellen</h2>
        </div>
        <p className="text-sm text-[#0D3B4C] mb-4">Hoeveel weken vooruit mogen klanten een ophaaldag kiezen?</p>
        <div className="flex items-end gap-3">
          <div>
            <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Aantal weken (1–{maxWeeks})</label>
            <input type="number" min="1" max={maxWeeks} value={bookingWeeks}
              onChange={(e) => setBookingWeeks(e.target.value)} data-testid="booking-weeks-input"
              className={`w-28 ${inputCls}`} />
          </div>
        </div>
      </div>

      {/* Capacity limits */}
      <div className="bg-[#FEF9E7] p-5">
        <div className="flex items-center gap-2 mb-1">
          <Users size={20} className="text-[#1B6B2E]" />
          <h2 className="font-[700] text-[#0D3B4C]">Capaciteitslimieten</h2>
        </div>
        <p className="text-sm text-[#0D3B4C] mb-4">Voorkom overvolle momenten. Zet op <b>0</b> voor onbeperkt. Een tijdslot of dag toont automatisch "Vol" zodra de limiet is bereikt.</p>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Max. porties per tijdslot</label>
            <input type="number" min="0" value={slotCapacity}
              onChange={(e) => setSlotCapacity(e.target.value)} data-testid="slot-capacity-input"
              className={`w-32 ${inputCls}`} />
          </div>
          <div>
            <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Max. bestellingen per dag</label>
            <input type="number" min="0" value={dailyLimit}
              onChange={(e) => setDailyLimit(e.target.value)} data-testid="daily-limit-input"
              className={`w-32 ${inputCls}`} />
          </div>
        </div>
      </div>

      {/* Individually blocked dates */}
      <div className="bg-[#FEF9E7] p-5">
        <div className="flex items-center gap-2 mb-1">
          <CalendarX2 size={20} className="text-[#B71C1C]" />
          <h2 className="font-[700] text-[#0D3B4C]">Losse geblokkeerde dagen</h2>
        </div>
        <p className="text-sm text-[#0D3B4C] mb-4">Blokkeer losse dagen (bijv. een feestdag), los van een vakantieperiode. Deze datums verdwijnen van de bestelpagina.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-[600] text-[#0D3B4C] mb-1">Datum</label>
            <input type="date" value={newBlocked} onChange={(e) => setNewBlocked(e.target.value)} data-testid="blocked-date-input" className={inputCls} />
          </div>
          <button type="button" onClick={addBlocked} data-testid="blocked-date-add"
            className="bg-[#1B6B2E] text-white font-[700] px-5 py-2 hover:bg-[#144F22] transition-colors">
            Toevoegen
          </button>
        </div>
        <div className="flex flex-wrap gap-2 mt-4" data-testid="blocked-date-list">
          {blockedDates.length === 0 ? (
            <span className="text-sm font-[600] text-[#0D3B4C]">Geen losse dagen geblokkeerd.</span>
          ) : (
            blockedDates.map((d) => (
              <span key={d} className="inline-flex items-center gap-2 bg-white border-2 border-[#B71C1C] px-3 py-1 text-sm font-[700] text-[#0D3B4C]" data-testid={`blocked-chip-${d}`}>
                {fmtDate(new Date(d + "T00:00:00"))}
                <button type="button" onClick={() => removeBlocked(d)} aria-label={`Verwijder ${d}`} data-testid={`blocked-remove-${d}`} className="text-[#B71C1C] hover:text-[#8E1515]">
                  <X size={15} />
                </button>
              </span>
            ))
          )}
        </div>
      </div>

      <button onClick={save} disabled={busy} data-testid="save-scheduling"
        className="flex items-center gap-2 bg-[#1B6B2E] text-white font-[700] uppercase tracking-wider px-6 py-3 hover:bg-[#144F22] transition-colors disabled:opacity-40">
        <Save size={18} /> Planning opslaan
      </button>
      {fetchedAt && (
        <p className="text-xs font-[600] text-[#0D3B4C]/70" data-testid="scheduling-last-updated">
          Laatst bijgewerkt: {fetchedAt.toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "medium" })}
        </p>
      )}
    </div>
  );
};
